import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConvites } from '../hooks/useConvites';
import { useEtapas, useProjetos, useTarefas } from '../hooks/useProjetos';
import { cores } from '../styles/cores';
import {
  Avatar,
  BadgeStatus,
  BarraProgresso,
  Btn,
  Campo,
  EstadoVazio,
  Input,
  Modal,
  Spinner,
} from '../components/UI';

const paraDataInput = (dataBR) => {
  if (!dataBR) return '';
  const [dia, mes, ano] = dataBR.split('/');
  if (!dia || !mes || !ano) return '';
  return `${ano}-${mes}-${dia}`;
};

const paraDataBR = (dataInput) => {
  if (!dataInput) return '';
  const [ano, mes, dia] = dataInput.split('-');
  return `${dia}/${mes}/${ano}`;
};

const COR_BORDA = {
  concluida: cores.sucesso,
  em_andamento: cores.primario,
  atrasada: cores.erro,
  aguardando: cores.textoTerciario,
};

function ChipsTarefas({ projetoId, etapaId }) {
  const { tarefas, carregando } = useTarefas(projetoId, etapaId);
  if (carregando || tarefas.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {tarefas.slice(-4).map((t) => (
        <span
          key={t.id}
          style={{
            fontSize: 12,
            padding: '4px 10px',
            borderRadius: 999,
            backgroundColor: t.concluida ? '#E1F5EE' : '#FCEFDB',
            color: t.concluida ? cores.sucesso : cores.alerta,
            fontWeight: 600,
          }}
        >
          {t.concluida ? '✓ ' : ''}{t.nome}
        </span>
      ))}
    </div>
  );
}

export function ProjetoDetalhe() {
  const { projetoId } = useParams();
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const { projetos, carregando: carregandoProjetos } = useProjetos();
  const { etapas, carregando: carregandoEtapas, criarEtapa, atualizarEtapa } = useEtapas(projetoId);
  const { enviarConvite } = useConvites(usuario?.uid, usuario?.email);

  const [modalEtapa, setModalEtapa] = useState(false);
  const [etapaEditando, setEtapaEditando] = useState(null);
  const [formEtapa, setFormEtapa] = useState({ nome: '', ordem: '', dataInicio: '', dataPrevisao: '' });
  const [salvandoEtapa, setSalvandoEtapa] = useState(false);

  const [modalConvite, setModalConvite] = useState(false);
  const [emailConvite, setEmailConvite] = useState('');
  const [enviandoConvite, setEnviandoConvite] = useState(false);
  const [erroConvite, setErroConvite] = useState('');

  const projeto = projetos.find((p) => p.id === projetoId);

  if (carregandoProjetos || carregandoEtapas) return <Spinner />;
  if (!projeto) return <EstadoVazio titulo="Projeto não encontrado" />;

  const ehDono = projeto.ownerUid === usuario?.uid;

  const abrirNovaEtapa = () => {
    setEtapaEditando(null);
    setFormEtapa({ nome: '', ordem: etapas.length + 1, dataInicio: '', dataPrevisao: '' });
    setModalEtapa(true);
  };

  const abrirEdicaoEtapa = (etapa) => {
    setEtapaEditando(etapa);
    setFormEtapa({
      nome: etapa.nome || '',
      ordem: etapa.ordem || 1,
      dataInicio: paraDataInput(etapa.dataInicio),
      dataPrevisao: paraDataInput(etapa.dataPrevisao),
    });
    setModalEtapa(true);
  };

  const salvarEtapa = async (e) => {
    e.preventDefault();
    setSalvandoEtapa(true);
    const dados = {
      nome: formEtapa.nome,
      ordem: Number(formEtapa.ordem),
      dataInicio: paraDataBR(formEtapa.dataInicio),
      dataPrevisao: paraDataBR(formEtapa.dataPrevisao),
    };
    if (etapaEditando) {
      await atualizarEtapa(etapaEditando.id, dados);
    } else {
      await criarEtapa(dados);
    }
    setSalvandoEtapa(false);
    setModalEtapa(false);
    setEtapaEditando(null);
  };

  const aoEnviarConvite = async (e) => {
    e.preventDefault();
    setErroConvite('');
    if (emailConvite.toLowerCase().trim() === usuario?.email?.toLowerCase().trim()) {
      setErroConvite('Você não pode convidar a si mesmo.');
      return;
    }
    setEnviandoConvite(true);
    await enviarConvite({
      projetoId: projeto.id,
      projetoNome: projeto.nome,
      paraEmail: emailConvite,
      deNome: usuario.nome || usuario.email,
    });
    setEnviandoConvite(false);
    setEmailConvite('');
    setModalConvite(false);
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: cores.textoSecundario, marginBottom: 16 }}>
        <span style={{ cursor: 'pointer', color: cores.primario }} onClick={() => navegar('/')}>
          Dashboard
        </span>
        {' > '}
        {projeto.nome}
      </div>

      {/* Hero */}
      <div
        style={{
          backgroundColor: '#fff',
          border: `1px solid ${cores.card}`,
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div style={{ flex: '1 1 300px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <h1 style={{ margin: 0, fontSize: 20, color: cores.texto }}>{projeto.nome}</h1>
            {!ehDono && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: cores.textoSecundario,
                  backgroundColor: cores.card,
                  padding: '3px 10px',
                  borderRadius: 999,
                }}
              >
                Convidado
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: cores.textoSecundario, marginBottom: 4 }}>
            Responsável: {projeto.responsavel || '—'} · Equipe: {(projeto.membros || []).length} pessoas · Categoria: {projeto.categoria}
          </div>
          <div style={{ fontSize: 13, color: cores.textoSecundario, marginBottom: 16 }}>
            Início: {projeto.dataInicio || '—'} → Previsão: {projeto.dataPrevisao || '—'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(projeto.membros || []).map((m) => (
              <Avatar key={m} nome={m} size={32} />
            ))}
          </div>
        </div>

        <div style={{ flex: '0 0 220px', textAlign: 'right' }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: cores.primario }}>
            {projeto.percentual || 0}%
            <span style={{ fontSize: 13, fontWeight: 600, color: cores.textoSecundario, marginLeft: 6 }}>
              concluído
            </span>
          </div>
          <div style={{ margin: '12px 0 6px' }}>
            <BarraProgresso percentual={projeto.percentual || 0} />
          </div>
          <div style={{ fontSize: 12, color: cores.textoSecundario }}>
            {projeto.tarefasConcluidas || 0} de {projeto.totalTarefas || 0} tarefas
          </div>
        </div>
      </div>

      {/* Barra de ações */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: cores.texto }}>Etapas do projeto</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {ehDono && (
            <Btn variante="secundario" onClick={() => setModalConvite(true)}>
              ✉️ Convidar colaborador
            </Btn>
          )}
          {ehDono && <Btn onClick={abrirNovaEtapa}>+ Nova etapa</Btn>}
        </div>
      </div>

      {/* Lista de etapas */}
      {etapas.length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma etapa cadastrada"
          descricao={ehDono ? 'Crie a primeira etapa para organizar as tarefas.' : 'O responsável pelo projeto ainda não criou etapas.'}
          acao={ehDono ? <Btn onClick={abrirNovaEtapa}>+ Nova etapa</Btn> : null}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {etapas.map((etapa) => {
            const corBorda = COR_BORDA[etapa.status] || cores.textoTerciario;
            const aguardando = etapa.status === 'aguardando';
            return (
              <div
                key={etapa.id}
                onClick={() => navegar(`/projetos/${projetoId}/etapas/${etapa.id}/tarefas`)}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${cores.card}`,
                  borderLeft: `4px solid ${corBorda}`,
                  borderRadius: 12,
                  padding: 18,
                  cursor: 'pointer',
                  opacity: aguardando ? 0.6 : 1,
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: corBorda,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {etapa.ordem}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 15, color: cores.texto }}>{etapa.nome}</h3>
                      <div style={{ fontSize: 12, color: cores.textoSecundario, marginTop: 2 }}>
                        {etapa.dataInicio || '—'} → {etapa.dataPrevisao || '—'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <strong style={{ color: corBorda }}>{etapa.percentual || 0}%</strong>
                      <BadgeStatus status={etapa.status} />
                      {ehDono && (
                        <Btn
                          variante="secundario"
                          onClick={(e) => { e.stopPropagation(); abrirEdicaoEtapa(etapa); }}
                        >
                          Editar
                        </Btn>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <BarraProgresso percentual={etapa.percentual || 0} cor={corBorda} />
                  </div>
                  <ChipsTarefas projetoId={projetoId} etapaId={etapa.id} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal etapa */}
      <Modal
        titulo={etapaEditando ? 'Editar etapa' : 'Nova etapa'}
        aberto={modalEtapa}
        aoFechar={() => setModalEtapa(false)}
      >
        <form onSubmit={salvarEtapa}>
          <Campo label="Nome*">
            <Input required value={formEtapa.nome} onChange={(e) => setFormEtapa({ ...formEtapa, nome: e.target.value })} />
          </Campo>
          <Campo label="Ordem">
            <Input type="number" min={1} required value={formEtapa.ordem} onChange={(e) => setFormEtapa({ ...formEtapa, ordem: e.target.value })} />
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Data de início">
              <Input type="date" value={formEtapa.dataInicio} onChange={(e) => setFormEtapa({ ...formEtapa, dataInicio: e.target.value })} />
            </Campo>
            <Campo label="Previsão">
              <Input type="date" value={formEtapa.dataPrevisao} onChange={(e) => setFormEtapa({ ...formEtapa, dataPrevisao: e.target.value })} />
            </Campo>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn type="button" variante="secundario" onClick={() => setModalEtapa(false)}>Cancelar</Btn>
            <Btn type="submit" disabled={salvandoEtapa}>{salvandoEtapa ? 'Salvando...' : 'Salvar'}</Btn>
          </div>
        </form>
      </Modal>

      {/* Modal convidar */}
      <Modal titulo="Convidar colaborador" aberto={modalConvite} aoFechar={() => setModalConvite(false)}>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: cores.textoSecundario }}>
          O convidado poderá ver o projeto e marcar tarefas como concluídas onde for responsável.
        </p>
        <form onSubmit={aoEnviarConvite}>
          {erroConvite && (
            <div style={{ backgroundColor: '#FBE6E6', color: cores.erro, padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
              {erroConvite}
            </div>
          )}
          <Campo label="E-mail do colaborador*">
            <Input
              type="email"
              required
              value={emailConvite}
              onChange={(e) => { setEmailConvite(e.target.value); setErroConvite(''); }}
              placeholder="email@exemplo.com"
            />
          </Campo>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn type="button" variante="secundario" onClick={() => setModalConvite(false)}>Cancelar</Btn>
            <Btn type="submit" disabled={enviandoConvite}>{enviandoConvite ? 'Enviando...' : 'Enviar convite'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
